import React, { useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '@sw/api-client';
import { ApiError } from '@sw/api-client';
import type { AuthMeResponse, SessionUser } from '@sw/tenant-types';
import { AuthContext, type AuthContextValue } from './AuthContext';

export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

export interface AuthProviderProps {
  apiClient: ApiClient;
  /** Path del endpoint de me. Default: `/api/client/auth/me`. */
  mePath?: string;
  /** Cuánto tiempo sin tocarlo se considera fresh. Default: 60s. */
  staleTimeMs?: number;
  children: ReactNode;
}

/**
 * Provee el auth state al árbol de componentes. Usa TanStack Query para fetchear
 * `GET /api/client/auth/me` al mount + refetch periódico (controlado por staleTime).
 *
 * Si el endpoint retorna 401, se interpreta como "no autenticado" (no es error). Cualquier
 * otro status falla con `error` propagado.
 *
 * Story SW-311.
 */
export function AuthProvider({
  apiClient,
  mePath = '/api/client/auth/me',
  staleTimeMs = 60_000,
  children,
}: AuthProviderProps) {
  const queryClient = useQueryClient();

  const query = useQuery<SessionUser | null, Error>({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: async () => {
      try {
        const response = await apiClient.get<AuthMeResponse>(mePath);
        // El backend devuelve { authenticated: true, sub, workspaceId, ... }.
        // Stripiamos `authenticated` para que el shape consumido sea SessionUser.
        const { authenticated: _ignore, ...sessionUser } = response;
        return sessionUser;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          // No autenticado — caso esperado, NO es error.
          return null;
        }
        throw err;
      }
    },
    staleTime: staleTimeMs,
    // En refetch automatic on window focus dejamos el default (false en este app
    // según el QueryClient global). Pero si en el futuro se activa, el /me es
    // suficientemente barato.
    retry: false,
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      user: query.data ?? null,
      isAuthenticated: query.data !== null && query.data !== undefined,
      isLoading: query.isPending,
      isFetching: query.isFetching,
      error: query.error,
      refetch: async () => {
        await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      },
    }),
    [query.data, query.isPending, query.isFetching, query.error, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
